import { useState, useEffect, useCallback } from 'react'
import { api } from '../../api/client'

// Real backing store for the Lead detail modal's Call Logs, Followups, and
// Call History widgets - see packages/db/src/goodday/LeadCallLog.ts,
// LeadFollowup.ts, and LeadCallHistory.ts for why this replaced
// localStorage-only mocks that fell back to hardcoded fake entries on every
// new lead. Mirrors useLeadMeetings.ts's shape for the same per-lead
// resource pattern.

export interface LeadActivityPerson {
  id: string
  name: string
  email?: string
  avatarUrl?: string
}

function toPerson(val: any): LeadActivityPerson | null {
  if (!val) return null
  const id = val._id ?? val.id ?? val
  if (typeof val !== 'object') return { id: String(val), name: 'Unknown' }
  return { id: String(id), name: val.name || 'Unknown', email: val.email, avatarUrl: val.avatarUrl }
}

// ===== Call Logs =====

export interface LeadCallLog {
  id: string
  type: 'Outbound' | 'Inbound'
  outcome: 'Connected' | 'Left Voicemail' | 'Busy' | 'No Answer' | 'Wrong Number' | 'Scheduled Demo'
  durationMinutes: number
  rep: LeadActivityPerson | null
  notes: string
  sentiment?: 'Positive' | 'Neutral' | 'Hesitant'
  occurredAt: string
}

export interface CreateLeadCallLogInput {
  type: LeadCallLog['type']
  outcome: LeadCallLog['outcome']
  durationMinutes?: number
  repUserId: string
  notes: string
  sentiment?: LeadCallLog['sentiment']
  occurredAt?: string
}

function fromCallLogRow(row: any): LeadCallLog {
  return {
    id: row._id ?? row.id,
    type: row.type,
    outcome: row.outcome,
    durationMinutes: row.durationMinutes ?? 0,
    rep: toPerson(row.repUserId),
    notes: row.notes,
    sentiment: row.sentiment,
    occurredAt: row.occurredAt,
  }
}

export function useLeadCallLogs(leadId: string | undefined) {
  const [callLogs, setCallLogs] = useState<LeadCallLog[]>([])
  const [loading, setLoading] = useState(true)

  const fetchLogs = useCallback(async () => {
    if (!leadId) {
      setCallLogs([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>(`/leads/${leadId}/call-logs`)
      setCallLogs((res?.data || []).map(fromCallLogRow))
    } catch (err) {
      console.error('Failed to load call logs', err)
      setCallLogs([])
    } finally {
      setLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const createCallLog = useCallback(
    async (input: CreateLeadCallLogInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.post<any>(`/leads/${leadId}/call-logs`, input, { skipErrorToast: true })
      const created = fromCallLogRow(res?.data)
      setCallLogs((prev) => [created, ...prev])
      return created
    },
    [leadId]
  )

  const deleteCallLog = useCallback(
    async (id: string) => {
      if (!leadId) throw new Error('leadId is required')
      await api.delete<any>(`/leads/${leadId}/call-logs/${id}`, { skipErrorToast: true })
      setCallLogs((prev) => prev.filter((c) => c.id !== id))
    },
    [leadId]
  )

  const updateCallLog = useCallback(
    async (id: string, input: Partial<CreateLeadCallLogInput>) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.patch<any>(`/leads/${leadId}/call-logs/${id}`, input, { skipErrorToast: true })
      const updated = fromCallLogRow(res?.data)
      setCallLogs((prev) => prev.map((c) => (c.id === id ? updated : c)))
      return updated
    },
    [leadId]
  )

  return { callLogs, loading, createCallLog, updateCallLog, deleteCallLog, refetch: fetchLogs }
}

// ===== Followups =====

export interface LeadFollowup {
  id: string
  title: string
  type: 'Call' | 'Email' | 'Meeting' | 'Demo' | 'Proposal'
  dueAt: string
  rep: LeadActivityPerson | null
  status: 'Pending' | 'Completed'
  priority: 'High' | 'Normal' | 'Low'
  notes?: string
}

export interface CreateLeadFollowupInput {
  title: string
  type?: LeadFollowup['type']
  dueAt: string
  repUserId: string
  priority?: LeadFollowup['priority']
  notes?: string
}

export interface UpdateLeadFollowupInput {
  title?: string
  type?: LeadFollowup['type']
  dueAt?: string
  repUserId?: string
  status?: LeadFollowup['status']
  priority?: LeadFollowup['priority']
  notes?: string
}

function fromFollowupRow(row: any): LeadFollowup {
  return {
    id: row._id ?? row.id,
    title: row.title,
    type: row.type ?? 'Call',
    dueAt: row.dueAt,
    rep: toPerson(row.repUserId),
    status: row.status ?? 'Pending',
    priority: row.priority ?? 'Normal',
    notes: row.notes,
  }
}

export function useLeadFollowups(leadId: string | undefined) {
  const [followups, setFollowups] = useState<LeadFollowup[]>([])
  const [loading, setLoading] = useState(true)

  const fetchFollowups = useCallback(async () => {
    if (!leadId) {
      setFollowups([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>(`/leads/${leadId}/followups`)
      setFollowups((res?.data || []).map(fromFollowupRow))
    } catch (err) {
      console.error('Failed to load followups', err)
      setFollowups([])
    } finally {
      setLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    fetchFollowups()
  }, [fetchFollowups])

  const createFollowup = useCallback(
    async (input: CreateLeadFollowupInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.post<any>(`/leads/${leadId}/followups`, input, { skipErrorToast: true })
      const created = fromFollowupRow(res?.data)
      setFollowups((prev) => [created, ...prev])
      return created
    },
    [leadId]
  )

  const updateFollowup = useCallback(
    async (id: string, input: UpdateLeadFollowupInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.patch<any>(`/leads/${leadId}/followups/${id}`, input, { skipErrorToast: true })
      const updated = fromFollowupRow(res?.data)
      setFollowups((prev) => prev.map((f) => (f.id === id ? updated : f)))
      return updated
    },
    [leadId]
  )

  const toggleFollowup = useCallback(
    async (id: string, currentStatus: LeadFollowup['status']) => {
      if (!leadId) throw new Error('leadId is required')
      const nextStatus = currentStatus === 'Completed' ? 'Pending' : 'Completed'
      const res = await api.patch<any>(`/leads/${leadId}/followups/${id}`, { status: nextStatus }, { skipErrorToast: true })
      const updated = fromFollowupRow(res?.data)
      setFollowups((prev) => prev.map((f) => (f.id === id ? updated : f)))
      return updated
    },
    [leadId]
  )

  const deleteFollowup = useCallback(
    async (id: string) => {
      if (!leadId) throw new Error('leadId is required')
      await api.delete<any>(`/leads/${leadId}/followups/${id}`, { skipErrorToast: true })
      setFollowups((prev) => prev.filter((f) => f.id !== id))
    },
    [leadId]
  )

  return { followups, loading, createFollowup, updateFollowup, toggleFollowup, deleteFollowup, refetch: fetchFollowups }
}

// ===== Call History =====

export interface LeadCallHistoryItem {
  id: string
  title: string
  rep: LeadActivityPerson | null
  occurredAt: string
  durationMinutes: number
  outcome: string
  sentiment: 'Positive' | 'Neutral' | 'Hesitant' | 'Urgent'
  summary: string
  keyPoints: string[]
  nextAction?: string
}

export interface CreateLeadCallHistoryInput {
  title: string
  repUserId: string
  occurredAt?: string
  durationMinutes?: number
  outcome: string
  sentiment?: LeadCallHistoryItem['sentiment']
  summary: string
  keyPoints?: string[]
  nextAction?: string
}

export type UpdateLeadCallHistoryInput = Partial<CreateLeadCallHistoryInput>

function fromCallHistoryRow(row: any): LeadCallHistoryItem {
  return {
    id: row._id ?? row.id,
    title: row.title,
    rep: toPerson(row.repUserId),
    occurredAt: row.occurredAt,
    durationMinutes: row.durationMinutes ?? 0,
    outcome: row.outcome,
    sentiment: row.sentiment ?? 'Neutral',
    summary: row.summary,
    keyPoints: row.keyPoints ?? [],
    nextAction: row.nextAction,
  }
}

export function useLeadCallHistory(leadId: string | undefined) {
  const [callHistory, setCallHistory] = useState<LeadCallHistoryItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchHistory = useCallback(async () => {
    if (!leadId) {
      setCallHistory([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>(`/leads/${leadId}/call-history`)
      setCallHistory((res?.data || []).map(fromCallHistoryRow))
    } catch (err) {
      console.error('Failed to load call history', err)
      setCallHistory([])
    } finally {
      setLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const createCallHistory = useCallback(
    async (input: CreateLeadCallHistoryInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.post<any>(`/leads/${leadId}/call-history`, input, { skipErrorToast: true })
      const created = fromCallHistoryRow(res?.data)
      setCallHistory((prev) => [created, ...prev])
      return created
    },
    [leadId]
  )

  const updateCallHistory = useCallback(
    async (id: string, input: UpdateLeadCallHistoryInput) => {
      if (!leadId) throw new Error('leadId is required')
      const res = await api.patch<any>(`/leads/${leadId}/call-history/${id}`, input, { skipErrorToast: true })
      const updated = fromCallHistoryRow(res?.data)
      setCallHistory((prev) => prev.map((h) => (h.id === id ? updated : h)))
      return updated
    },
    [leadId]
  )

  const deleteCallHistory = useCallback(
    async (id: string) => {
      if (!leadId) throw new Error('leadId is required')
      await api.delete<any>(`/leads/${leadId}/call-history/${id}`, { skipErrorToast: true })
      setCallHistory((prev) => prev.filter((h) => h.id !== id))
    },
    [leadId]
  )

  return { callHistory, loading, createCallHistory, updateCallHistory, deleteCallHistory, refetch: fetchHistory }
}

// ===== Call Recordings =====
// The audio is held in the database, not on disk, and the API only hands it
// out to a Super Admin, so this hook is mounted for that role alone.

export interface LeadCallRecordingItem {
  id: string
  filename: string
  mime: string
  sizeBytes: number
  uploadedBy: LeadActivityPerson | null
  createdAt: string
}

function fromRecordingRow(row: any): LeadCallRecordingItem {
  return {
    id: row._id ?? row.id,
    filename: row.filename,
    mime: row.mime,
    sizeBytes: row.sizeBytes ?? 0,
    uploadedBy: toPerson(row.uploadedBy),
    createdAt: row.createdAt,
  }
}

export function useLeadCallRecordings(leadId: string | undefined) {
  const [recordings, setRecordings] = useState<LeadCallRecordingItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchRecordings = useCallback(async () => {
    if (!leadId) {
      setRecordings([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>(`/leads/${leadId}/call-recordings`, undefined, { skipErrorToast: true })
      setRecordings((res?.data || []).map(fromRecordingRow))
    } catch (err) {
      console.error('Failed to load call recordings', err)
      setRecordings([])
    } finally {
      setLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    fetchRecordings()
  }, [fetchRecordings])

  const uploadRecording = useCallback(
    async (file: File) => {
      if (!leadId) throw new Error('leadId is required')
      const form = new FormData()
      form.append('file', file)
      const res = await api.post<any>(`/leads/${leadId}/call-recordings`, form, { headers: { 'Content-Type': null }, skipErrorToast: true })
      const created = fromRecordingRow(res?.data)
      setRecordings((prev) => [created, ...prev])
      return created
    },
    [leadId]
  )

  const deleteRecording = useCallback(
    async (id: string) => {
      if (!leadId) throw new Error('leadId is required')
      await api.delete<any>(`/leads/${leadId}/call-recordings/${id}`, { skipErrorToast: true })
      setRecordings((prev) => prev.filter((r) => r.id !== id))
    },
    [leadId]
  )

  return { recordings, loading, uploadRecording, deleteRecording, refetch: fetchRecordings }
}

/** The bytes for one recording, as a blob the browser can play or save. */
export function fetchRecordingBlob(leadId: string, id: string): Promise<Blob> {
  return api.get<Blob>(`/leads/${leadId}/call-recordings/${id}/file`, undefined, { responseType: 'blob', skipErrorToast: true })
}
